import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface User {
  id: string
  email: string
  full_name: string
  first_name?: string
  last_name?: string
  job_title?: string
  phone?: string
  department?: string
  country?: string
  role: 'owner' | 'admin' | 'editor' | 'viewer'
  organization_id: string
  organization_name?: string
  is_super_admin?: boolean
  is_manager?: boolean
  manager_id?: string | null
  can_share_internally?: boolean
  can_create_external_share_links?: boolean
  can_download_gif?: boolean
  can_download_mp4?: boolean
  can_use_sales_enablement?: boolean
  sales_enablement_enabled?: boolean
  organization_sales_enablement_enabled?: boolean
}

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
}

const _devToken = (() => {
  try {
    return (import.meta as any).env?.DEV ? (import.meta as any).env?.VITE_DEV_TOKEN || null : null
  } catch {
    return null
  }
})()
const _storedToken = localStorage.getItem('token') || _devToken
if (_devToken && !localStorage.getItem('token')) {
  try {
    localStorage.setItem('token', _devToken)
  } catch {
    // ignore local dev storage failures
  }
}
const _storedUser = (() => {
  try { return JSON.parse(localStorage.getItem('user') || 'null') } catch { return null }
})()

const initialState: AuthState = {
  user: _storedToken ? _storedUser : null,
  token: _storedToken,
  isAuthenticated: !!_storedToken,
  isLoading: !!_storedToken && !_storedUser,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ user: User; token: string; refreshToken?: string; freshLogin?: boolean }>) => {
      state.user = action.payload.user
      state.token = action.payload.token
      state.isAuthenticated = true
      state.isLoading = false
      localStorage.setItem('token', action.payload.token)
      localStorage.setItem('user', JSON.stringify(action.payload.user))
      if (action.payload.refreshToken) {
        localStorage.setItem('refresh_token', action.payload.refreshToken)
      }
      // Start a fresh idle window, but ONLY for a real login.
      //
      // The bug: closing a tab never clears bms_last_activity_at, so a stale
      // value survives. useSessionTimeout reads it the moment MainLayout
      // mounts, and a user signing in after a long gap was signed straight
      // back out with "You were signed out after the configured period of
      // inactivity" before touching anything. Expiring the session is what
      // finally cleared the key, which is why the SECOND login worked.
      //
      // Why this is gated on freshLogin: ProtectedRoute also dispatches
      // setCredentials on every mount to refresh permissions, so stamping
      // unconditionally would reset the idle clock on every page load and
      // defeat the timeout entirely — an idle tab could be revived just by
      // refreshing. Only the paths that actually exchanged credentials
      // (login, OAuth callback, registration) start a new window.
      if (action.payload.freshLogin) {
        localStorage.setItem('bms_last_activity_at', String(Date.now()))
      }
      window.dispatchEvent(new CustomEvent('auth:session-established'))
    },
    updateAccessToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload
      state.isAuthenticated = true
      localStorage.setItem('token', action.payload)
    },
    logout: (state) => {
      state.user = null
      state.token = null
      state.isAuthenticated = false
      localStorage.removeItem('token')
      localStorage.removeItem('refresh_token')
      localStorage.removeItem('user')
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
  },
})

export const { setCredentials, updateAccessToken, logout, setLoading } = authSlice.actions
export default authSlice.reducer
